/**
 * Settings Page - Phase 6 + 8.3 + 8.4 + 8.6
 *
 * Tabbed interface for managing:
 * - Organization/company information
 * - Financial settings (tax, invoice defaults, proposal defaults)
 * - Branding (logo, colors, payment instructions)
 * - Billable item templates (Phase 8.6)
 * - Users (admin only - Phase 8.4)
 *
 * Architecture: API-only access (no direct Firestore from frontend)
 */
import { useMemo } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { useSearchParams } from 'react-router-dom';
import { ViewTabs, ViewTab } from '@/components/ui/view-tabs';
import { Settings, Building2, Palette, Tags, FileStack, DollarSign, Filter, Receipt, Users, Mail, Bell, User, Plug, FileText, Layers, ClipboardList, Key, SwatchBook } from 'lucide-react';
import { AccountSettingsTab } from '@/components/settings/AccountSettingsTab';
import { OrganizationSettingsTab } from '@/components/settings/OrganizationSettingsTab';
import { FinancialSettingsTab } from '@/components/settings/FinancialSettingsTab';
import { BrandingSettingsTab } from '@/components/settings/BrandingSettingsTab';
import { ScopeTypesTab } from '@/components/settings/ScopeTypesTab';
import { TemplatesTab } from './settings/TemplatesTab';
import { FeeAssumptionsTab } from './settings/FeeAssumptionsTab';
import { BillableItemTemplatesTab } from './settings/BillableItemTemplatesTab';
import { ProjectCategoriesTab } from '@/components/settings/ProjectCategoriesTab';
import { ProjectListColorsTab } from '@/components/settings/ProjectListColorsTab';
import { EmailSettingsTab } from '@/components/settings/EmailSettingsTab';
import { UsersTab } from '@/components/settings/UsersTab';
import { NotificationPreferencesTab } from '@/components/settings/NotificationPreferencesTab';
import { IntegrationsTab } from '@/components/settings/IntegrationsTab';
import { ProposalSettingsTab } from '@/components/settings/ProposalSettingsTab';
import { SiteVisitSettingsTab } from '@/components/settings/SiteVisitSettingsTab';
import { ModulesSettingsTab } from '@/components/settings/ModulesSettingsTab';
import { DocumentRequirementsTab } from '@/components/settings/DocumentRequirementsTab';
import { ApiTokensTab } from '@/components/settings/ApiTokensTab';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useFeature } from '@/lib/features/FeatureProvider';

// Base settings tabs configuration (always visible)
const baseSettingsTabs: ViewTab[] = [
  { value: 'account', label: 'Account', icon: User },
  { value: 'organization', label: 'Organization', icon: Building2 },
  { value: 'financial', label: 'Financial', icon: DollarSign },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'notifications', label: 'Notifications', icon: Bell },
  { value: 'branding', label: 'Branding', icon: Palette },
  { value: 'scope-types', label: 'Client Types', icon: Tags },
  { value: 'project-categories', label: 'Project Categories', icon: Filter },
  { value: 'project-list-colours', label: 'Project List Colours', icon: SwatchBook },
  { value: 'templates', label: 'Project Templates', icon: FileStack },
  { value: 'fee-assumptions', label: 'Fee Assumptions', icon: DollarSign },
  { value: 'billable-item-templates', label: 'Billable Item Templates', icon: Receipt },
  { value: 'proposal-settings', label: 'Proposal Settings', icon: FileText },
  { value: 'site-visit-settings', label: 'Field Review Settings', icon: ClipboardList },
  { value: 'integrations', label: 'Integrations', icon: Plug },
  { value: 'modules', label: 'Modules', icon: Layers },
  // API Tokens - restored 2026-06-26. The temporary PAT_PEPPER-debug hide
  // (2026-05-09) left the self-serve Claude connector card (added 2026-06-22)
  // behind an unreachable tab. PAT_PEPPER is configured on prod and the path
  // works, so the tab is restored. The connector card within is gated on
  // ai_connector + mcp_access; PAT creation is scoped to the user's role.
  { value: 'api-tokens', label: 'API Tokens', icon: Key },
];

// Users tab (admin only)
const usersTab: ViewTab = { value: 'users', label: 'Users', icon: Users };

export function SettingsPage() {
  const { can } = useAuth();
  const hasDocReqs = useFeature('document_requirements_engine');
  const [searchParams, setSearchParams] = useSearchParams();

  // Build tabs based on user permissions and feature flags
  const settingsTabs = useMemo(() => {
    const tabs = [...baseSettingsTabs];
    // Add Users tab for users with user_management permission
    if (can('user_management')) {
      tabs.push(usersTab);
    }
    // Add Document Requirements tab when feature flag is on
    if (hasDocReqs) {
      tabs.push({ value: 'document-requirements', label: 'Document Requirements', icon: ClipboardList });
    }
    return tabs;
  }, [can, hasDocReqs]);

  // Read active tab from URL params (default: account).
  // If returning from Dropbox OAuth (?dropbox=connected/error), show integrations tab.
  const activeTab = searchParams.get('tab') || (searchParams.get('dropbox') ? 'integrations' : 'account');

  // Handle tab change
  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return <AccountSettingsTab />;
      case 'organization':
        return <OrganizationSettingsTab />;
      case 'financial':
        return <FinancialSettingsTab />;
      case 'email':
        return <EmailSettingsTab />;
      case 'notifications':
        return <NotificationPreferencesTab />;
      case 'branding':
        return <BrandingSettingsTab />;
      case 'scope-types':
        return <ScopeTypesTab />;
      case 'project-categories':
        return <ProjectCategoriesTab />;
      case 'project-list-colours':
        return <ProjectListColorsTab />;
      case 'templates':
        return <TemplatesTab />;
      case 'fee-assumptions':
        return <FeeAssumptionsTab />;
      case 'billable-item-templates':
        return <BillableItemTemplatesTab />;
      case 'proposal-settings':
        return <ProposalSettingsTab />;
      case 'site-visit-settings':
        return <SiteVisitSettingsTab />;
      case 'users':
        return <UsersTab />;
      case 'integrations':
        return <IntegrationsTab />;
      case 'modules':
        return <ModulesSettingsTab />;
      case 'document-requirements':
        return <DocumentRequirementsTab />;
      case 'api-tokens':
        return <ApiTokensTab />;
      default:
        return <AccountSettingsTab />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Settings className="w-6 h-6 text-slate-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
            <p className="text-gray-600 mt-1">
              Manage your organization, invoice defaults, branding, templates, and fee assumptions
            </p>
          </div>
        </div>

        {/* Tabs */}
        <ViewTabs
          views={settingsTabs}
          currentView={activeTab}
          onChange={handleTabChange}
        />

        {/* Tab Content */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          {renderTabContent()}
        </div>
      </div>
    </DashboardLayout>
  );
}
